import { ColorPicker } from '@/components/inputs/ColorPicker';
import { ImagePicker } from '@/components/inputs/ImagePicker/ImagePicker';
import { BUILTIN_IMG_SRC } from '@/components/inputs/ImagePicker/source';
import { DEFAULT_FONT_FAMILY, FONT_FAMILIES } from '@/constant';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { PLACEHOLDER_STYLE } from '@/style';
import { Coordinate, isSlideComponentDndDragZone, PropsWithPreview, reposition, Size, SlideComponentDndDragZone, SlideComponentDndDragZoneImage, SlideComponentDndDragZoneText } from '@/types/slide';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FlipIcon from '@mui/icons-material/Flip';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutline';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TitleIcon from '@mui/icons-material/Title';
import { Box, InputAdornment, OutlinedInput, Popover, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ComponentLabel } from '../ComponentLabel';
import { ContextualBar } from '../ContextualBar';
import { useRightClickMenu } from '../hooks/useRightClickMenu';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { ImageContent } from '../image/ImageContent';
import { RightClickMenu } from '../RightClickMenu';
import { SlideRef } from '../Slide';
import { TextContent } from '../SlideComponentText/TextContent';
import { TypeSettingsPopover } from '../SlideComponentText/TypeSettingsPopover';

interface Props {
    init: {
        focus?: boolean;
    };
    model: SlideComponentDndDragZone;
    onDeleteObject?: (id: string) => void;
    onUpdateObject?: (id: string, component: SlideComponentDndDragZone) => void;
    onUploadImage?: (file: File) => Promise<string>;
    onBringToFrontHandler?: (id: string) => void;
    onBringToBackHandler?: (id: string) => void;
    onUndo?: (id: string) => void;
    onRedo?: (id: string) => void;
    onUpdateTag?: (dragModel: SlideComponentDndDragZone, tag: string) => void;
    tagColor?: string;
    question?: SlideComponentDndDragZone;
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentDndDragObject: React.FC<PropsWithPreview<Props>> = ({
    init,
    model,
    preview,
    onDeleteObject,
    onUpdateObject,
    onUploadImage,
    onBringToBackHandler,
    onBringToFrontHandler,
    onUndo,
    onRedo,
    onUpdateTag,
    tagColor,
    question,
    slideRef,
}) => {
    const { t } = useTranslation();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    const mainRef = useRef<HTMLDivElement | null>(null);

    // Add refs for the popovers
    const imagePickerRef = useRef<HTMLDivElement | null>(null);
    const typeSettingsRef = useRef<HTMLDivElement | null>(null);
    const backgroundColorPickerRef = useRef<HTMLDivElement | null>(null);

    const { isSelected } = useSlideComponent(mainRef, [], !!preview);

    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(question ? question.pos : model.pos);

    // Add state for image picker popover
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);
    const [typeSettingsAnchorEl, setTypeSettingsAnchorEl] = useState<HTMLElement | null>(null);
    const [backgroundColorAnchorEl, setBackgroundColorAnchorEl] = useState<HTMLElement | null>(null);
    const openBackgroundColorPicker = Boolean(backgroundColorAnchorEl);

    const [aspectRatio, setAspectRatio] = useState(model.size.width / model.size.height);

    const isEditing = isFocus || isSelected;

    const debouncedUpdateComponent = useDebounce(onUpdateObject || (() => {}), 10);

    useOutsideClick(mainRef, () => {
        if (preview) return;
        if (!mainRef) return;

        setIsFocus(false);
    });

    useEffect(() => {
        if (isFocus && !preview) {
            setImagePickerAnchorEl(mainRef.current);
        }
    }, [isFocus, preview]);

    if (!isSlideComponentDndDragZone(model)) {
        return null;
    }

    const addImageHandler = () => {
        const updateModel = { ...model, type: 'image' as const, content: { image: '', imageURL: '', flip: 1 } } as SlideComponentDndDragZoneImage;
        debouncedUpdateComponent?.(model.id, { ...updateModel });
    };

    const addTextHandler = () => {
        const updateModel: SlideComponentDndDragZoneText = {
            ...model,
            type: 'text' as const,
            content: {
                text: '',
                fontSize: 16,
                jyutping: [],
                color: '#000000',
                typeSettings: {
                    fontFamily: FONT_FAMILIES[0]?.fontFamily ?? DEFAULT_FONT_FAMILY,
                    formatting: [],
                    alignment: 'center',
                    isPhonetic: false,
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                },
            },
        };
        debouncedUpdateComponent?.(model.id, updateModel);
    };

    return (
        <Box
            onContextMenu={(e) => {
                if (isEditing) {
                    e.stopPropagation();
                    handleRightClickMenu(e);
                }
            }}
            ref={mainRef}
            id={`dnd-drag-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={(e) => {
                if (preview) return;
                e.stopPropagation();
                if (model.type === 'image') {
                    setImagePickerAnchorEl(mainRef.current);
                } else if (model.type === 'text') {
                    setTypeSettingsAnchorEl(mainRef.current);
                }
            }}
        >
            <ResizeBorder
                minSize={{ width: 80, height: 40 }}
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                aspectRatio={model.type === 'text' || model.type === 'default' ? 0 : aspectRatio} // free resize for text
                slideRef={slideRef}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        outline: '4px solid',
                        outlineColor: '#6B6762',
                        backgroundColor:
                            model.type === 'text' && model.content?.typeSettings?.backgroundColor && model.content?.typeSettings?.backgroundColor !== 'transparent'
                                ? (model.content?.typeSettings?.backgroundColor ?? 'transparent')
                                : (model.settings?.backgroundColor ?? 'transparent'),
                        overflow: 'hidden',
                    }}
                >
                    {!preview && (
                        <ComponentLabel
                            variant='caption'
                            sx={{
                                ...(!isEditing && {
                                    opacity: 0, // Hide by default
                                    transition: 'opacity 0.2s ease',
                                    [`#dnd-drag-component-${model.id}:hover &`]: {
                                        opacity: 1, // Show when text-component is hovered
                                    },
                                }),
                            }}
                            noWrap
                        >
                            {t('slide.components.dnd.dragObject')}
                        </ComponentLabel>
                    )}
                    {model.type === 'default' && (
                        <Box
                            sx={{
                                backgroundColor: model.settings?.backgroundColor ?? 'transparent',
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                                overflow: 'hidden',
                            }}
                        >
                            <Stack
                                alignItems='center'
                                justifyContent='center'
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    ...PLACEHOLDER_STYLE,
                                }}
                            >
                                <Typography align='center'>{t('slide.components.dnd.addImage')}</Typography>
                                <Typography align='center'>{t('slide.components.dnd.or')}</Typography>
                                <Typography align='center'>{t('slide.components.dnd.inputText')}</Typography>
                            </Stack>
                        </Box>
                    )}

                    {model.type === 'image' && <ImageContent model={model} />}

                    {model.type === 'text' && (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                            <TextContent
                                preview={preview}
                                autoFocus={isEditing}
                                model={model}
                                onUpdate={(v) => {
                                    const updatedContent = { ...model.content, text: v };
                                    onUpdateObject?.(model.id, { ...model, content: updatedContent });
                                }}
                            />
                        </Box>
                    )}
                </Box>

                {!preview && isEditing && (
                    <ContextualBar
                        anchor='right'
                        startButtons={[
                            ...(model.type === 'default'
                                ? [
                                      {
                                          icon: <ImageIcon />,
                                          tooltip: t('slide.components.dnd.addImage'),
                                          onClick: () => {
                                              addImageHandler();
                                              setImagePickerAnchorEl(mainRef.current);
                                          },
                                      },
                                      {
                                          icon: <TextFieldsIcon />,
                                          tooltip: t('slide.components.dnd.inputText'),
                                          onClick: () => {
                                              addTextHandler();
                                              // setImagePickerAnchorEl(mainRef.current);
                                          },
                                      },
                                  ]
                                : []),
                            ...(model.type === 'image'
                                ? [
                                      {
                                          icon: <EditIcon />,
                                          tooltip: t('slide.components.dnd.tooltip.edit'),
                                          onClick: () => {
                                              setImagePickerAnchorEl(mainRef.current);
                                          },
                                      },
                                      {
                                          icon: <FlipIcon />,
                                          tooltip: t('slide.components.dnd.tooltip.flip'),
                                          onClick: () => {
                                              const updatedContent = { ...model.content, flip: model.content.flip === 1 ? -1 : 1 };
                                              const updateModel = { ...model, content: updatedContent };
                                              onUpdateObject?.(model.id, updateModel);
                                          },
                                      },
                                  ]
                                : []),
                            ...(model.type === 'text'
                                ? [
                                      {
                                          icon: <TitleIcon />,
                                          tooltip: t('slide.components.assets.text.tooltip.typeSettings'),
                                          onClick: () => {
                                              setTypeSettingsAnchorEl(mainRef.current);
                                          },
                                      },
                                  ]
                                : []),
                            {
                                icon: <FormatColorFillIcon />,
                                tooltip: t('slide.components.dnd.tooltip.backgroundColor'),
                                onClick: () => {
                                    setBackgroundColorAnchorEl(mainRef.current);
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('slide.components.dnd.tooltip.remove'),
                                onClick: () => {
                                    onDeleteObject?.(model.id);
                                },
                            },
                        ]}
                    />
                )}

                {model.type === 'image' && imagePickerAnchorEl && (
                    <ImagePicker
                        type='image'
                        title={t('slide.components.dnd.title')}
                        librarySources={BUILTIN_IMG_SRC}
                        anchorEl={imagePickerAnchorEl}
                        slotProps={{
                            paper: {
                                ref: imagePickerRef, // Attach the ref to the Paper element
                            },
                        }}
                        value={{
                            value: model.content?.image,
                            imageURL: model.content?.imageURL,
                        }}
                        onClose={() => {
                            setImagePickerAnchorEl(null);
                            setIsFocus(false);
                        }}
                        onUpdate={(settings) => {
                            const updatedContent = { ...model.content, image: settings.value, imageURL: settings.imageURL };

                            let updateModel = { ...model, content: updatedContent, ...(settings.size ? { size: settings.size } : {}) };
                            updateModel = reposition(updateModel);

                            setTPos(updateModel.pos);
                            setTSize(updateModel.size);
                            setAspectRatio(updateModel.size.width / updateModel.size.height);

                            onUpdateObject?.(model.id, updateModel);
                        }}
                        onUpload={onUploadImage}
                    />
                )}

                {model.type === 'text' && typeSettingsAnchorEl && (
                    <TypeSettingsPopover
                        anchorEl={typeSettingsAnchorEl}
                        slotProps={{
                            paper: {
                                ref: typeSettingsRef, // Attach the ref to the Paper element
                                sx: {
                                    p: 2,
                                },
                            },
                        }}
                        value={{
                            isPhonetic: model?.content?.typeSettings?.isPhonetic,
                            fontFamily: model?.content?.typeSettings?.fontFamily,
                            fontSize: model?.content?.fontSize,
                            formatting: model?.content?.typeSettings?.formatting,
                            alignment: model?.content?.typeSettings?.alignment,
                            color: model?.content?.color,
                            borderRadius: model?.content?.typeSettings?.borderRadius,
                            backgroundColor: model?.content?.typeSettings?.backgroundColor,
                        }}
                        onClose={() => {
                            setTypeSettingsAnchorEl(null);
                        }}
                        onUpdate={(settings) => {
                            const typeSettingsContent = {
                                isPhonetic: settings.isPhonetic,
                                fontFamily: settings.fontFamily,
                                alignment: settings.alignment,
                                formatting: settings.formatting,
                                borderRadius: settings.borderRadius,
                                backgroundColor: settings.backgroundColor,
                            };

                            const updatedContent = { ...model.content, fontSize: settings.fontSize, typeSettings: typeSettingsContent, color: settings.color };
                            debouncedUpdateComponent?.(model.id, { ...model, content: updatedContent });
                        }}
                    />
                )}

                {openBackgroundColorPicker && (
                    <Popover
                        open={openBackgroundColorPicker}
                        anchorEl={backgroundColorAnchorEl}
                        onClose={() => setBackgroundColorAnchorEl(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: -8,
                            horizontal: 'center',
                        }}
                        slotProps={{
                            paper: {
                                ref: backgroundColorPickerRef, // Attach the ref to the Paper element
                            },
                        }}
                    >
                        <Box sx={{ p: 2 }}>
                            <ColorPicker
                                value={model.settings.backgroundColor}
                                onChange={(color: string) => {
                                    debouncedUpdateComponent?.(model.id, { ...model, settings: { backgroundColor: color } });
                                }}
                            />
                        </Box>
                    </Popover>
                )}
            </ResizeBorder>

            {(!preview || (typeof preview === 'object' && preview.answer)) && (
                <OutlinedInput
                    onChange={(e) => {
                        onUpdateTag?.(model, e.target.value);
                    }}
                    disabled={!!preview}
                    defaultValue={model.tag}
                    placeholder={t('slide.components.dnd.tag')}
                    inputProps={{
                        style: {
                            textAlign: 'center',
                            pointerEvents: 'auto',
                        },
                    }}
                    sx={{
                        mt: 1,
                        borderRadius: 100,
                        width: '100%',
                        p: 0.25,
                        color: 'black',
                        pointerEvents: 'auto',
                        backgroundColor: tagColor ? tagColor : (theme) => (theme.vars || theme).palette.grey[50],
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                    endAdornment={
                        preview ? undefined : (
                            <InputAdornment position='end' sx={{ cursor: 'help' }}>
                                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('slide.components.dnd.tooltip.tag')}</span>}>
                                    <HelpOutlinedIcon fontSize='small' />
                                </Tooltip>
                            </InputAdornment>
                        )
                    }
                    startAdornment={
                        preview ? undefined : (
                            <InputAdornment position='start' sx={{ cursor: 'default' }}>
                                <HelpOutlinedIcon fontSize='small' sx={{ color: 'transparent' }} />
                            </InputAdornment>
                        )
                    }
                />
            )}

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                undo={onUndo}
                redo={onRedo}
            />
        </Box>
    );
};
